import { APIResponseType } from 'app/apiService/types';
import apiService from '..';
import { ChatRequest, ChatResponse, OperationsRequest, OperationsResponse, GenerateEmailRequest, GenerateEmailResponse, InitRequest, InitResponse, UpdateConversationStateRequest, UpdateConversationStateResponse } from './types';
import { getMockChatResponse, mockChatResponses, mockGenerateEmailResponse, mockInitResponse } from './mock';
import { 
  isMockModeEnabled, 
  getMockDelay, 
  shouldLogApiCalls, 
  shouldLogMockResponses 
} from './config';

// NOTE: This service uses existing mock data from ./mock.ts for development/testing
// The mock system provides realistic chat flow responses based on different actions
// Mock mode can be controlled via the configuration system

class MiaChatV2ApiService {
  static ENDPOINT = 'api/v2/mia';

  /**
   * Get available operations for a dealer
   * @param payload - Request payload containing dealer_id
   * @returns Promise with operations response
   */
  static async getOperations(payload: OperationsRequest): Promise<APIResponseType<OperationsResponse>> {
    try {
      if (!payload.dealer_id) {
        throw new Error("Dealer ID is required");
      }

      // Check if mock mode is enabled
      if (isMockModeEnabled()) {
        if (shouldLogApiCalls()) {
          console.log('🔴 MOCK MODE: getOperations called with payload:', payload);
        }
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, getMockDelay()));
        
        // Return mock operations data
        const mockOperations: OperationsResponse = {
          next_action: "SELECT_OPERATION",
          operations: [
            {
              id: "1",
              display_text: "Generate Lease Offer",
              description: "Create personalized lease offers for customers",
              tags: ["Lease", "Offers", "Sales"],
              action: "SELECT_OPERATION",
              icon: "lease"
            },
            {
              id: "2", 
              display_text: "Generate Finance Offer",
              description: "Create personalized finance offers for customers",
              tags: ["Finance", "Offers", "Sales"],
              action: "SELECT_OPERATION",
              icon: "finance"
            },
            {
              id: "3",
              display_text: "Generate Trade-In Offer", 
              description: "Create personalized trade-in offers for customers",
              tags: ["Trade-In", "Offers", "Sales"],
              action: "SELECT_OPERATION",
              icon: "trade-in"
            }
          ]
        };

        const mockApiResponse: APIResponseType<OperationsResponse> = {
          body: mockOperations,
          statusCode: 200,
          statusText: 'OK',
          message: 'Mock operations data'
        };

        if (shouldLogMockResponses()) {
          console.log('🔴 MOCK MODE: getOperations response:', mockApiResponse);
        }

        return mockApiResponse;
      }

      // Real API call when mock mode is disabled
      if (shouldLogApiCalls()) {
        console.log('🟢 REAL API: getOperations called with payload:', payload);
      }

      const response = await apiService.post<OperationsResponse>(
        `${this.ENDPOINT}/operations`,
        payload
      );
      return response;
    } catch (error) {
      console.error('Error fetching operations:', error);
      throw error;
    }
  }

  /**
   * Initialize a new chat conversation
   * @param payload - Request payload containing dealer_id
   * @returns Promise with chat initialization response
   */
  static async initChat(payload: InitRequest, signal?: AbortSignal): Promise<APIResponseType<InitResponse>> {
    try {
      if (!payload.dealer_id) {
        throw new Error("Dealer ID is required");
      }

      // Check if mock mode is enabled
      if (isMockModeEnabled()) {
        if (shouldLogApiCalls()) {
          console.log('🔴 MOCK MODE: initChat called with payload:', payload);
        }
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, getMockDelay()));
        
        // Return mock init data

        const mockApiResponse: APIResponseType<InitResponse> = {
          body: mockInitResponse,
          statusCode: 201,
          statusText: 'Created',
          message: 'Chat conversation initialized successfully'
        };

        if (shouldLogMockResponses()) {
          console.log('🔴 MOCK MODE: initChat response:', mockApiResponse);
        }

        return mockApiResponse;
      }

      // Real API call when mock mode is disabled
      if (shouldLogApiCalls()) {
        console.log('🟢 REAL API: initChat called with payload:', payload);
      }

      const response = await apiService.post<InitResponse>(
        `${this.ENDPOINT}/chat/init`,
        payload,
        { signal }
      );
      
      if (shouldLogApiCalls()) {
        console.log('🟢 REAL API: initChat response:', response);
      }
      
      return response;
    } catch (error) {
      console.error('Error initializing chat:', error);
      throw error;
    }
  }

  /**
   * Send chat message and get response
   * @param payload - Request payload containing chat details
   * @returns Promise with chat response
   */
  static async sendChatMessage(payload: ChatRequest, signal?: AbortSignal): Promise<APIResponseType<ChatResponse>> {
    try {
      if (!payload.dealer_id) {
        throw new Error("Dealer ID is required");
      }

      if (!payload.conversation_id) {
        throw new Error("Conversation ID is required");
      }

      // Check if mock mode is enabled
      if (isMockModeEnabled()) {
        if (shouldLogApiCalls()) {
          console.log('🔴 MOCK MODE: sendChatMessage called with payload:', payload);
        }

        // Use existing mock data based on action
        let mockResponse = getMockChatResponse(payload.selected_option || '0');
        
        if (!mockResponse) {
          // Fallback mock response if no specific mock data found
          mockResponse = mockChatResponses['0'];
        }
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, getMockDelay()));

        const mockApiResponse: APIResponseType<ChatResponse> = {
          body: mockResponse,
          statusCode: 200,
          statusText: 'OK',
          message: `Mock response for action: ${payload.action || 'chat'}`
        };

        if (shouldLogMockResponses()) {
          console.log('🔴 MOCK MODE: sendChatMessage response:', mockApiResponse);
        }

        return mockApiResponse;
      }

      // Real API call when mock mode is disabled
      if (shouldLogApiCalls()) {
        console.log('🟢 REAL API: sendChatMessage called with payload:', payload);
      }

      const response = await apiService.post<ChatResponse>(
        `${this.ENDPOINT}/chat`,
        payload,
        { signal }
      );
      
      if (shouldLogApiCalls()) {
        console.log('🟢 REAL API: sendChatMessage response:', response);
      }
      
      return response;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  }

  /**
   * Start a new chat conversation
   * @param payload - Request payload for new chat
   * @returns Promise with chat response
   */
  static async startNewChat(payload: ChatRequest, signal?: AbortSignal): Promise<APIResponseType<ChatResponse>> {
    try {
      if (!payload.dealer_id) {
        throw new Error("Dealer ID is required");
      }

      if (!payload.conversation_id) {
        throw new Error("Conversation ID is required");
      }

      if (!payload.action) {
        throw new Error("Action is required for new chat");
      }

      // Check if mock mode is enabled
      if (isMockModeEnabled()) {
        if (shouldLogApiCalls()) {
          console.log('🔴 MOCK MODE: startNewChat called with payload:', payload);
        }

        let mockResponse: ChatResponse;
        
        // Use mock data for new chat
        mockResponse = mockChatResponses['0'];
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, getMockDelay()));

        const mockApiResponse: APIResponseType<ChatResponse> = {
          body: mockResponse,
          statusCode: 200,
          statusText: 'OK',
          message: `Mock response for action: ${payload.action || 'chat'}`
        };

        if (shouldLogMockResponses()) {
          console.log('🔴 MOCK MODE: startNewChat response:', mockApiResponse);
        }

        return mockApiResponse;
      }

      // Real API call when mock mode is disabled
      if (shouldLogApiCalls()) {
        console.log('🟢 REAL API: startNewChat called with payload:', payload);
      }

      const response = await apiService.post<ChatResponse>(
        `${this.ENDPOINT}/chat`,
        { ...payload, messages: ['']}
      );
      
      if (shouldLogApiCalls()) {
        console.log('🟢 REAL API: startNewChat response:', response);
      }
      
      return response;
    } catch (error) {
      console.error('Error starting new chat:', error);
      throw error;
    }
  }

  /**
   * Generate email from conversation
   * @param payload - Request payload containing dealer_id, conversation_id, and messages
   * @returns Promise with email response containing HTML
   */
  static async generateEmail(payload: GenerateEmailRequest, signal?: AbortSignal): Promise<APIResponseType<GenerateEmailResponse>> {
    try {
      if (!payload.dealer_id) {
        throw new Error("Dealer ID is required");
      }

      if (!payload.conversation_id) {
        throw new Error("Conversation ID is required");
      }

      if (!payload.messages || payload.messages.length === 0) {
        throw new Error("Messages are required");
      }

      // Check if mock mode is enabled
      if (isMockModeEnabled()) {
        if (shouldLogApiCalls()) {
          console.log('🔴 MOCK MODE: generateEmail called with payload:', payload);
        }
        
        // Simulate API delay - 5 seconds for email generation
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        // Return mock email data with HTML
        const mockApiResponse: APIResponseType<GenerateEmailResponse> = {
          body: mockGenerateEmailResponse,
          statusCode: 200,
          statusText: 'OK',
          message: 'Mock email generated successfully'
        };

        if (shouldLogMockResponses()) {
          console.log('🔴 MOCK MODE: generateEmail response:', mockApiResponse);
        }

        return mockApiResponse;
      }

      // Real API call when mock mode is disabled
      if (shouldLogApiCalls()) {
        console.log('🟢 REAL API: generateEmail called with payload:', payload);
      }

      const response = await apiService.post<GenerateEmailResponse>(
        `${this.ENDPOINT}/email`,
        payload,
        { signal }
      );
      
      if (shouldLogApiCalls()) {
        console.log('🟢 REAL API: generateEmail response:', response);
      }
      
      return response;
    } catch (error) {
      console.error('Error generating email:', error);
      throw error;
    }
  }

  /**
   * Update conversation state with email template ID
   * @param payload - Request payload containing dealer_id, conversation_id, and state
   * @returns Promise with update response
   */
  static async updateConversationState(payload: UpdateConversationStateRequest, signal?: AbortSignal): Promise<APIResponseType<UpdateConversationStateResponse>> {
    try {
      if (!payload.dealer_id) {
        throw new Error("Dealer ID is required");
      }

      if (!payload.conversation_id) {
        throw new Error("Conversation ID is required");
      }

      if (!payload.state?.generated_email_template_id) {
        throw new Error("Generated email template ID is required");
      }

      const response = await apiService.put<UpdateConversationStateResponse>(
        `${this.ENDPOINT}/chat/conversation`,
        payload,
        { signal }
      );
      
      return response;
    } catch (error) {
      console.error('Error updating conversation state:', error);
      throw error;
    }
  }

  /**
   * Invalidate cache for chat-related endpoints
   * @param conversationId - Optional conversation ID to invalidate specific chat
   */
  static async invalidateChatCache(conversationId?: string): Promise<void> {
    try {
      if (conversationId) {
        // Invalidate specific conversation cache
        apiService.invalidateCacheByPrefix(`${this.ENDPOINT}/chat`, 'POST');
      } else {
        // Invalidate all chat-related cache
        apiService.invalidateCacheByPrefix(this.ENDPOINT, 'POST');
        apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      }
    } catch (error) {
      console.error('Error invalidating chat cache:', error);
    }
  }
}

export default MiaChatV2ApiService;
